import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { DWM_API_URL } from 'config';

// Dark Web Monitoring (Trillion) API.
// Every endpoint is tenancy-scoped: the API's ValidationFilter reads the
// `tenancyId` query param, verifies the caller can access it and resolves the
// matching Trillion organisationId, so `tenancyId` must always be supplied.
// Auth is JWT Bearer from localStorage, matching the other services. Breach
// feeds refresh infrequently, so responses are cached for 6 hours.
const SIX_HOURS = 6 * 60 * 60;

const baseQuery = fetchBaseQuery({
    baseUrl: DWM_API_URL,
    prepareHeaders: (headers) => {
        const user = localStorage.getItem('user');
        if (user) {
            try {
                const userToken = JSON.parse(user).token;
                headers.set('authorization', `Bearer ${userToken}`);
                headers.set('Content-Type', 'application/json');
            } catch (e) {
                console.error('Error parsing user token:', e);
            }
        }
        return headers;
    },
    credentials: "omit"
});

export const dwmApi = createApi({
    reducerPath: "dwmApi",
    baseQuery: baseQuery,
    keepUnusedDataFor: SIX_HOURS,
    tagTypes: ["DwmStats", "DwmBreachedUsers", "DwmBreachTrend", "DwmBreachPeriodStats"],
    endpoints: (builder) => ({
        // GET /getorgstats?tenancyId= -> { breachcount: OrganisationStats }
        // We unwrap the `breachcount` envelope and hand back the stats object.
        getDwmOrgStats: builder.query({
            query: ({ tenancyId }) => `getorgstats?tenancyId=${tenancyId}`,
            transformResponse: (response) => response?.breachcount ?? null,
            transformErrorResponse: (response) => response?.status,
            providesTags: (result, error, { tenancyId }) => [{ type: "DwmStats", id: tenancyId }]
        }),

        // GET /breachstats?tenancyId=&startDate=&endDate=
        //   -> { allTimeRows, totalMentions, credMentions, distinctEmails }
        // One call feeds all four tiles: allTimeRows is the static all-time figure
        // (cached server-side); totalMentions/credMentions/distinctEmails are windowed
        // by match date (created_at) to the requested range.
        getDwmBreachStats: builder.query({
            query: ({ tenancyId, startDate, endDate }) => {
                const params = new URLSearchParams({ tenancyId });
                if (startDate) params.set("startDate", startDate);
                if (endDate) params.set("endDate", endDate);
                return `breachstats?${params.toString()}`;
            },
            transformResponse: (response) => response ?? { allTimeRows: 0, totalMentions: 0, credMentions: 0, distinctEmails: 0 },
            transformErrorResponse: (response) => response?.status,
            providesTags: (result, error, { tenancyId }) => [{ type: "DwmBreachPeriodStats", id: tenancyId }]
        }),

        // GET /breachedusers?tenancyId=&page=&limit=&sortKey=&sortDirection=&startDate=&endDate=
        // -> { users: [...], pagination: {...} }
        // startDate/endDate window the list by match date (created_at); omit for all-time.
        getDwmBreachedUsers: builder.query({
            query: ({ tenancyId, page = 1, limit = 100, sortKey, sortDirection = "DESC", startDate, endDate }) => {
                const params = new URLSearchParams({ tenancyId, page, limit, sortDirection });
                if (sortKey) params.set("sortKey", sortKey);
                if (startDate) params.set("startDate", startDate);
                if (endDate) params.set("endDate", endDate);
                return `breachedusers?${params.toString()}`;
            },
            transformResponse: (response) => response ?? { users: [], pagination: null },
            transformErrorResponse: (response) => response?.status,
            providesTags: (result, error, { tenancyId }) => [{ type: "DwmBreachedUsers", id: tenancyId }]
        }),

        // GET /breachtrend?tenancyId=&startDate=&endDate=&granularity=
        //   -> { granularity, baseline: { mentions }, points: [ { bucket, mentions } ] }
        // Complete, zero-filled, ordered breach-mention series for the range; echoes the
        // granularity it chose (for granularity="auto") and the pre-window cumulative baseline.
        getDwmBreachTrend: builder.query({
            query: ({ tenancyId, startDate, endDate, granularity = "auto" }) => {
                const params = new URLSearchParams({ tenancyId, granularity });
                if (startDate) params.set("startDate", startDate);
                if (endDate) params.set("endDate", endDate);
                return `breachtrend?${params.toString()}`;
            },
            transformResponse: (response) => response ?? { granularity: "month", baseline: { mentions: 0 }, points: [] },
            transformErrorResponse: (response) => response?.status,
            providesTags: (result, error, { tenancyId }) => [{ type: "DwmBreachTrend", id: tenancyId }]
        }),
    })
});

export const {
    useGetDwmOrgStatsQuery,
    useLazyGetDwmOrgStatsQuery,
    useGetDwmBreachStatsQuery,
    useLazyGetDwmBreachStatsQuery,
    useGetDwmBreachedUsersQuery,
    useLazyGetDwmBreachedUsersQuery,
    useGetDwmBreachTrendQuery,
    useLazyGetDwmBreachTrendQuery
} = dwmApi;
